import type { ReactNode } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  FileText,
  Info,
  MapPin,
  Printer,
  ShieldCheck,
  XCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  PrintLabelChoice,
  PrintPrinterChoice,
  PrintResult,
  PrintTemplateChoice,
} from '@/api/labelPrintingApi'
import type { PrinterFamily } from '@/lib/labelPrinting'
import type { PrintSummary } from '@/lib/labelPrinting'

// The parts of the print popup (PrintLabelDialog). Presentational only: what to
// print and where is decided by the dialog and the server.

const FAMILY_TILE: Record<PrinterFamily, string> = {
  zebra: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  isbt: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
}

/** A numbered step heading: 1 Printer, 2 Layout, 3 Check. */
export function StepHeading({ step, title, aside }: { step: number; title: string; aside?: ReactNode }) {
  return (
    <div className='mb-2 flex items-center gap-2'>
      <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background'>
        {step}
      </span>
      <h3 className='text-sm font-semibold'>{title}</h3>
      {aside && <span className='ml-auto text-xs text-muted-foreground'>{aside}</span>}
    </div>
  )
}

/** The family's icon tile, also used in the dialog header. */
export function FamilyTile({ family, className }: { family: PrinterFamily; className?: string }) {
  return (
    <span className={cn('flex shrink-0 items-center justify-center rounded-lg', FAMILY_TILE[family], className)}>
      <Printer className='h-4 w-4' aria-hidden />
    </span>
  )
}

/** One printer as a selectable card. A printer with nothing approved to print is shown but cannot be picked. */
export function PrinterCard({
  printer,
  selected,
  onSelect,
}: {
  printer: PrintPrinterChoice
  selected: boolean
  onSelect: () => void
}) {
  const usable = printer.templates.length > 0
  return (
    <button
      type='button'
      role='radio'
      aria-checked={selected}
      aria-disabled={!usable}
      disabled={!usable}
      onClick={onSelect}
      className={cn(
        'group relative w-full rounded-xl border bg-background p-3 text-left transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'border-primary shadow-sm ring-2 ring-primary/20'
          : usable && 'hover:-translate-y-px hover:border-foreground/25 hover:shadow-sm',
        !usable && 'cursor-not-allowed opacity-60',
      )}
    >
      <div className='flex items-start gap-3'>
        <FamilyTile family={printer.family} className='h-9 w-9' />
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
            <span className='truncate font-medium'>{printer.displayName}</span>
            {printer.signatureRequired && (
              <span className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'>
                <ShieldCheck className='h-3 w-3' aria-hidden />
                Sign to print
              </span>
            )}
          </div>
          <p className='mt-0.5 flex items-center gap-1 text-xs text-muted-foreground'>
            <MapPin className='h-3 w-3 shrink-0' aria-hidden />
            <span className='truncate'>{printer.location ?? 'No location set'}</span>
          </p>
          <div className='mt-2 flex flex-wrap items-center gap-1.5 text-[11px]'>
            <span className='rounded-md bg-muted px-1.5 py-0.5 font-medium'>
              {printer.loadedStock}
              {printer.resolution && ` · ${printer.resolution}`}
            </span>
            <span className='truncate font-mono text-muted-foreground'>{printer.destination}</span>
          </div>
          {!usable && (
            <p className='mt-1.5 text-xs text-amber-700 dark:text-amber-400'>
              No approved layout fits the label loaded in it.
            </p>
          )}
        </div>
        <CheckCircle2
          className={cn('h-5 w-5 shrink-0 text-primary transition-opacity', selected ? 'opacity-100' : 'opacity-0')}
          aria-hidden
        />
      </div>
    </button>
  )
}

/** The printer's approved layouts as pills. */
export function LayoutPicker({
  templates,
  value,
  onChange,
}: {
  templates: PrintTemplateChoice[]
  value: string | null
  onChange: (id: string) => void
}) {
  return (
    <div role='radiogroup' aria-label='Layout' className='flex flex-wrap gap-2'>
      {templates.map(t => {
        const on = t.id === value
        return (
          <button
            key={t.id}
            type='button'
            role='radio'
            aria-checked={on}
            onClick={() => onChange(t.id)}
            title={t.fileName ?? undefined}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              on ? 'border-foreground bg-foreground text-background' : 'hover:bg-muted',
            )}
          >
            {t.name}
            {t.version !== null && (
              <span className={cn('text-[11px]', on ? 'text-background/70' : 'text-muted-foreground')}>v{t.version}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** "Label 2 of 5" with arrows, for a batch. */
export function LabelStepper({
  labels,
  index,
  onChange,
}: {
  labels: PrintLabelChoice[]
  index: number
  onChange: (i: number) => void
}) {
  if (labels.length < 2) return null
  const label = labels[index]
  return (
    <div className='mb-3 flex items-center gap-2 rounded-lg border bg-muted/30 px-2 py-1.5'>
      <button
        type='button'
        className='rounded-md p-1 hover:bg-background disabled:opacity-40'
        onClick={() => onChange(index - 1)}
        disabled={index === 0}
        aria-label='Previous label'
      >
        <ChevronLeft className='h-4 w-4' />
      </button>
      <div className='min-w-0 flex-1 text-center'>
        <p className='truncate text-sm font-medium'>{label.title}</p>
        <p className='text-[11px] text-muted-foreground'>
          Label {index + 1} of {labels.length}
        </p>
      </div>
      <button
        type='button'
        className='rounded-md p-1 hover:bg-background disabled:opacity-40'
        onClick={() => onChange(index + 1)}
        disabled={index === labels.length - 1}
        aria-label='Next label'
      >
        <ChevronRight className='h-4 w-4' />
      </button>
    </div>
  )
}

/** A muted desk the label sits on, so a white label reads as paper. */
export function LabelDesk({ children }: { children: ReactNode }) {
  return (
    <div className='rounded-xl border bg-[radial-gradient(circle,hsl(var(--muted-foreground)/0.18)_1px,transparent_1px)] bg-muted/40 p-4 [background-size:14px_14px]'>
      {children}
    </div>
  )
}

/** ISBT labels are laid out on the print computer, so there is nothing to draw. */
export function NoIsbtPreview({ template }: { template: PrintTemplateChoice | null }) {
  return (
    <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed bg-background/80 px-4 py-6 text-center'>
      <FileText className='h-6 w-6 text-muted-foreground' aria-hidden />
      <p className='text-sm font-medium'>No preview for ISBT labels</p>
      <p className='max-w-xs text-xs text-muted-foreground'>
        HemaTrax lays out the label on the print computer, from the values below.
      </p>
      {template?.fileName && (
        <span className='rounded-md bg-muted px-2 py-0.5 font-mono text-[11px]'>{template.fileName}</span>
      )}
    </div>
  )
}

/** What the label says, in words. */
export function FactsList({ facts }: { facts: PrintLabelChoice['facts'] }) {
  if (facts.length === 0) return null
  return (
    <div>
      <p className='mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground'>On this label</p>
      <dl className='grid grid-cols-1 gap-x-4 gap-y-2 rounded-lg border p-3 sm:grid-cols-2'>
        {facts.map(f => (
          <div key={f.label} className='min-w-0'>
            <dt className='text-[11px] text-muted-foreground'>{f.label}</dt>
            <dd className='break-words text-sm font-medium'>{f.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** Why this label cannot go to this kind of printer, and what to do instead. */
export function ProblemCallout({ problems, hasBrowser }: { problems: string[]; hasBrowser: boolean }) {
  if (problems.length === 0) return null
  return (
    <div
      role='alert'
      className='flex gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
    >
      <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
      <div className='space-y-1'>
        {problems.map(p => (
          <p key={p}>{p}</p>
        ))}
        <p className='text-xs opacity-80'>
          {hasBrowser ? 'Print in the browser instead, or ' : ''}ask an admin to check Printer association in
          Integrations, Printers.
        </p>
      </div>
    </div>
  )
}

/** A quiet heads-up: the label still prints, the print service may refuse it. */
export function WarningNote({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null
  return (
    <div role='note' className='flex gap-2 px-1 text-xs text-muted-foreground'>
      <Info className='mt-px h-3.5 w-3.5 shrink-0' aria-hidden />
      <div className='space-y-0.5'>
        {warnings.map(w => (
          <p key={w}>{w}</p>
        ))}
      </div>
    </div>
  )
}

const OUTCOME_ICON = {
  accepted: <CheckCircle2 className='h-4 w-4 text-emerald-600' aria-label='Sent' />,
  skipped: <CircleSlash className='h-4 w-4 text-muted-foreground' aria-label='Not sent' />,
  unavailable: <XCircle className='h-4 w-4 text-destructive' aria-label='Not sent' />,
  rejected: <XCircle className='h-4 w-4 text-destructive' aria-label='Not sent' />,
  timeout: <AlertTriangle className='h-4 w-4 text-amber-600' aria-label='No answer' />,
  failed: <XCircle className='h-4 w-4 text-destructive' aria-label='Not sent' />,
} as const

/** How the print went: one headline, then each label when there are several or one failed. */
export function PrintResultPanel({ summary, results }: { summary: PrintSummary; results: PrintResult[] }) {
  const ok = summary.tone === 'ok'
  return (
    <div
      role={ok ? 'status' : 'alert'}
      className={cn(
        'animate-in fade-in-0 zoom-in-95 rounded-xl border p-4',
        ok
          ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10'
          : 'border-destructive/30 bg-destructive/5',
      )}
    >
      <div className='flex items-start gap-3'>
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            ok ? 'bg-emerald-600 text-white' : 'bg-destructive text-destructive-foreground',
          )}
        >
          {ok ? <CheckCircle2 className='h-5 w-5' aria-hidden /> : <XCircle className='h-5 w-5' aria-hidden />}
        </span>
        <div className='min-w-0'>
          <p className='font-semibold'>{summary.title}</p>
          <p className='text-sm text-muted-foreground'>{summary.detail}</p>
        </div>
      </div>
      {(results.length > 1 || !ok) && (
        <ul className='mt-3 divide-y rounded-lg border bg-background/70 text-sm'>
          {results.map(r => (
            <li key={r.id} className='flex items-center gap-2 px-3 py-2'>
              {OUTCOME_ICON[r.outcome]}
              <span className='min-w-0 flex-1 truncate'>{r.title}</span>
              {r.printJobId && <code className='font-mono text-[11px] text-muted-foreground'>{r.printJobId}</code>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
